<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid>
      <div class="flex flex-col space-y-6">
        <!-- Back Navigation -->
        <v-btn
          class="rl-back-btn"
          outlined
          color="primary"
          @click="$router.push({ name: 'SecurityAssuranceCase' })"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('sac.backToCases') }}
        </v-btn>

        <div class="rl-content-wrapper">
          <!-- Search Bar -->
          <v-card class="rl-search-bar-container" elevation="2">
            <div class="rl-search-bar-header">
              <v-icon color="white" size="20" class="mr-2">mdi-magnify</v-icon>
              <span class="white--text text-subtitle-2 font-weight-bold">Search Requirements</span>
            </div>
            <div class="rl-search-bar-body">
              <div class="flex flex-row items-center space-x-4">
                <v-select
                  v-model="searchField"
                  :items="searchFields"
                  item-text="text"
                  item-value="value"
                  :label="$t('search-field')"
                  hide-details
                  outlined
                  dense
                  class="mb-4 search-field"
                  style="max-width: 200px;"
                ></v-select>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  :label="$t('search')"
                  hide-details
                  class="mb-4 flex-grow-1"
                  outlined
                  dense
                  @keyup.enter="applySearch"
                />
              </div>
            </div>
          </v-card>

          <!-- Data Table Card -->
          <v-card class="rl-table-card" elevation="2">
            <div class="rl-table-header">
              <v-icon color="white" size="20" class="mr-2">mdi-table</v-icon>
              <span class="white--text text-subtitle-2 font-weight-bold">Requirements</span>
            </div>

            <v-data-table
              :headers="getHeaders"
              :items="inScopeRequirements"
              :search="search"
              :custom-filter="customFilter"
              class="elevation-1 certifai-table"
              :loading="loading"
              :items-per-page="5"
              :footer-props="{
                showFirstLastPage: true,
                firstIcon: 'mdi-arrow-collapse-left',
                lastIcon: 'mdi-arrow-collapse-right',
                prevIcon: 'mdi-arrow-left',
                nextIcon: 'mdi-arrow-right',
                disableItemsPerPage: false,
                itemsPerPageText: getItemsPerPageText,
                showCurrentPage: true,
              }"
              must-sort
              :sort-desc="true"
            >
              <template v-slot:[`item.status`]="{ item }">
                <v-chip :color="statusChipColor(item.status)" outlined dark>
                  <span class="text-capitalize">{{ $t(item.status) }}</span>
                </v-chip>
              </template>
              <template v-slot:[`item.origin`]="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on">
                      {{ item.origin === 'spider' ? 'mdi-spider' : 'mdi-account-box' }}
                    </v-icon>
                  </template>
                  <span>{{
                    item.origin === 'spider' ? 'Created by Threat Spider' : 'Created Manually'
                  }}</span>
                </v-tooltip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex align-center justify-center gap-1">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        class="g-action-btn g-action-info"
                        v-on="on"
                        v-bind="attrs"
                        @click="openReviewModal(item)"
                      >
                        <v-icon small>mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('Details') }}</span>
                  </v-tooltip>
                  <v-tooltip top v-if="item.status === 'Draft' || 'Pending'">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        class="g-action-btn g-action-add"
                        v-on="on"
                        v-bind="attrs"
                        @click="enableCreateCaeDialog(item)"
                      >
                        <v-icon small>mdi-plus-thick</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('createCae') }}</span>
                  </v-tooltip>
                  <v-tooltip top v-if="item.status !== 'Draft'">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        class="g-action-btn g-action-edit"
                        v-on="on"
                        v-bind="attrs"
                        @click="handleEditRequirement(item)"
                      >
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('edit') }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        class="g-action-btn g-action-ai"
                        v-on="on"
                        v-bind="attrs"
                        @click="navigateToChat(item)"
                      >
                        <v-icon small>mdi-creation</v-icon>
                      </v-btn>
                    </template>
                    <span>SAC Composer Assistance</span>
                  </v-tooltip>
                  <v-tooltip top v-if="item.status !== 'Draft'">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        class="g-action-btn g-action-navigate"
                        v-on="on"
                        v-bind="attrs"
                        @click="handleClickedRequirement(item)"
                      >
                        <v-icon small>mdi-arrow-right-bold</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('To Tree View') }}</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </v-card>

          <!-- Review Modal -->
          <v-dialog v-model="reviewModalOpen" max-width="900" scrollable>
            <v-card class="rl-dialog-card" v-if="selectedRequirement">
              <v-card-title
                class="rl-review-header white--text d-flex justify-space-between align-center"
              >
                <span class="text-h5 font-weight-bold">Requirement Review</span>
                <v-btn icon @click="reviewModalOpen = false" color="white">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text class="pa-6">
                <v-container>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="pa-4 mb-4 info-card">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="primary" class="mr-2">mdi-identifier</v-icon>
                          <div class="text-subtitle-2 font-weight-bold">ID</div>
                        </div>
                        <div class="text-body-1 ml-8">{{ selectedRequirement.id }}</div>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-card outlined class="pa-4 mb-4 info-card">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="primary" class="mr-2">mdi-tag</v-icon>
                          <div class="text-subtitle-2 font-weight-bold">Requirement ID</div>
                        </div>
                        <div class="text-body-1 ml-8">
                          {{ selectedRequirement.requirementId }}
                        </div>
                      </v-card>
                    </v-col>

                    <v-col cols="12">
                      <v-card outlined class="pa-4 mb-4 info-card">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="primary" class="mr-2">mdi-format-title</v-icon>
                          <div class="text-subtitle-2 font-weight-bold">Title</div>
                        </div>
                        <div class="text-body-1 ml-8">{{ selectedRequirement.title }}</div>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-card outlined class="pa-4 mb-4 info-card">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="primary" class="mr-2">mdi-folder-open</v-icon>
                          <div class="text-subtitle-2 font-weight-bold">Family</div>
                        </div>
                        <div class="text-body-1 ml-8">{{ selectedRequirement.family }}</div>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-card outlined class="pa-4 mb-4 info-card">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="primary" class="mr-2">mdi-checkbox-marked-circle</v-icon>
                          <div class="text-subtitle-2 font-weight-bold">Status</div>
                        </div>
                        <div class="ml-8">
                          <v-chip :color="statusChipColor(selectedRequirement.status)" outlined>
                            <span class="text-capitalize">{{ selectedRequirement.status }}</span>
                          </v-chip>
                        </div>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-card outlined class="pa-4 mb-4 info-card">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="primary" class="mr-2">
                            {{
                              selectedRequirement.origin === 'spider'
                                ? 'mdi-spider'
                                : 'mdi-account-box'
                            }}
                          </v-icon>
                          <div class="text-subtitle-2 font-weight-bold">Origin</div>
                        </div>
                        <div class="text-body-1 ml-8">
                          {{ selectedRequirement.origin === 'spider' ? 'Threat Spider' : 'Manual' }}
                        </div>
                      </v-card>
                    </v-col>

                    <v-col cols="12">
                      <v-card outlined class="pa-4 info-card">
                        <div class="d-flex align-center mb-2">
                          <v-icon color="primary" class="mr-2">mdi-text-box</v-icon>
                          <div class="text-subtitle-2 font-weight-bold">Description</div>
                        </div>
                        <div class="text-body-2 ml-8 mt-2" style="line-height: 1.6; color: #555;">
                          {{ selectedRequirement.description || 'No description available' }}
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions class="pa-4 bg-secondary-50">
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="reviewModalOpen = false">
                  Close
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-dialog
            v-model="createCaeClicked"
            :max-width="createCaeItem && createCaeItem.status === 'Draft' ? 680 : 520"
            persistent
          >
            <v-card class="cae-dialog-card">
              <!-- Dialog Header -->
              <div class="cae-dialog-header">
                <div class="d-flex align-center">
                  <div class="cae-header-icon-wrap mr-3">
                    <v-icon color="white" size="22">mdi-file-document-edit-outline</v-icon>
                  </div>
                  <div>
                    <div class="white--text text-subtitle-1 font-weight-bold">
                      {{
                        createCaeItem && createCaeItem.status === 'Draft'
                          ? 'Create Cases-Argumentation-Evidences'
                          : 'Add Cases-Argumentation-Evidences'
                      }}
                    </div>
                    <div class="white--text text-caption" style="opacity: 0.8;">
                      Select a method to build your CAE
                    </div>
                  </div>
                </div>
                <v-btn icon small @click="createCaeClicked = false">
                  <v-icon color="white" size="20">mdi-close</v-icon>
                </v-btn>
              </div>

              <!-- Requirement Info Banner -->
              <div v-if="createCaeItem" class="cae-req-banner">
                <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                  <v-chip
                    :color="statusChipColor(createCaeItem.status)"
                    x-small
                    dark
                    class="font-weight-bold"
                  >
                    {{ createCaeItem.status }}
                  </v-chip>
                  <span class="text-subtitle-2 font-weight-bold primary--text">
                    {{ createCaeItem.id }}
                  </span>
                  <span class="cae-req-divider">—</span>
                  <span class="text-body-2 cae-req-title">{{ createCaeItem.title }}</span>
                </div>
              </div>

              <!-- Method Selection -->
              <v-card-text class="pa-5">
                <div
                  class="text-caption text-uppercase font-weight-bold mb-4"
                  style="color: #78909c; letter-spacing: 0.08em;"
                >
                  Choose creation method
                </div>
                <v-row
                  no-gutters
                  :class="
                    createCaeItem && createCaeItem.status === 'Draft'
                      ? 'cae-methods-3'
                      : 'cae-methods-2'
                  "
                >
                  <!-- Upload JSON -->
                  <v-col
                    cols="12"
                    :sm="createCaeItem && createCaeItem.status === 'Draft' ? 6 : 12"
                    class="pa-2"
                  >
                    <v-card
                      class="cae-method-card"
                      outlined
                      @click="
                        $router.push({ name: 'JsonView', query: { requirement: createCaeItem } })
                      "
                    >
                      <v-card-text class="text-center pa-5">
                        <div
                          class="cae-method-icon-wrap mb-3"
                          style="background: rgba(88, 30, 92, 0.08);"
                        >
                          <v-icon color="primary" size="28">mdi-code-braces</v-icon>
                        </div>
                        <div class="text-subtitle-2 font-weight-bold mb-1">
                          Create using Json template
                        </div>
                        <div class="text-caption" style="color: #78909c;">
                          Import a structured CAE from a JSON file with visual graph preview
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <!-- Create Manually -->
                  <v-col
                    v-if="createCaeItem && createCaeItem.status === 'Draft'"
                    cols="12"
                    sm="6"
                    class="pa-2"
                  >
                    <v-card
                      class="cae-method-card"
                      outlined
                      @click="handleManualClick(createCaeItem)"
                    >
                      <v-card-text class="text-center pa-5">
                        <div
                          class="cae-method-icon-wrap mb-3"
                          style="background: rgba(25, 118, 210, 0.08);"
                        >
                          <v-icon color="blue darken-1" size="28">mdi-pencil-box-outline</v-icon>
                        </div>
                        <div class="text-subtitle-2 font-weight-bold mb-1">Create Manually</div>
                        <div class="text-caption" style="color: #78909c;">
                          Build your CAE step-by-step using the guided claims editor
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>

              <!-- Footer -->
              <div class="cae-dialog-footer">
                <v-btn text small color="grey darken-1" @click="createCaeClicked = false">
                  Cancel
                </v-btn>
              </div>
            </v-card>
          </v-dialog>
        </div>
      </div>
    </v-container>
  </v-main>
</template>

<script>
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus.js';
import inScopeRequirementsApi from '../api/inScopeRequirementsApi';
import Loader from '@/components/helpers/Loader';

export default {
  name: 'RequirementsList',
  components: {
    Loader,
  },
  mixins: [util],
  computed: {
    getItemsPerPageText() {
      const t = this.$t.bind(this);
      return t('requirements.table-footer');
    },
    getHeaders() {
      const t = this.$t.bind(this);
      return [
        {
          text: t('requirements.headers.id'),
          value: 'id',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '8%',
        },
        {
          text: 'Requirement ID',
          value: 'requirementId',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('requirements.headers.title'),
          value: 'title',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('requirements.headers.family'),
          value: 'family',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('requirements.headers.status'),
          value: 'status',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: this.$t('Origin'),
          value: 'origin',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('requirements.headers.actions'),
          value: 'actions',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '15%',
        },
      ];
    },
    searchFields() {
      const t = this.$t.bind(this);
      return [
        { text: t('search-field-all'), value: 'all' },
        { text: t('requirements.headers.id'), value: 'id' },
        { text: 'Requirement ID', value: 'requirementId' },
        { text: t('requirements.headers.title'), value: 'title' },
        { text: t('requirements.headers.family'), value: 'family' },
        { text: t('requirements.headers.status'), value: 'status' },
        { text: this.$t('Origin'), value: 'origin' },
      ];
    },
  },
  props: {
    socId: {
      type: String,
      required: true,
    },
    socData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      inScopeRequirements: [],
      search: null,
      searchField: 'all',
      createCaeClicked: false,
      createCaeItem: null,
      reviewModalOpen: false,
      selectedRequirement: null,
    };
  },
  created() {
    this.$emit('loading', true);
    this.loading = true;
    this.fetchInScopeRequirements();
    this.$emit('loading', false);
    this.loading = false;
  },
  methods: {
    async fetchInScopeRequirements() {
      try {
        const { data: rawInScopeRequirements } = await inScopeRequirementsApi.all(this.socId);
        this.inScopeRequirements = rawInScopeRequirements;
      } catch (error) {
        if (error.response.status === 400) {
          EventBus.$emit('snackbar', {
            text: this.$t('errorlist.error-404'),
            color: 'grey',
          });
        }
      }
    },
    enableCreateCaeDialog(item) {
      this.createCaeItem = item;
      this.createCaeClicked = true;
    },
    byteToUint8Array(byteArray) {
      var uint8Array = new Uint8Array(byteArray.length);
      for (var i = 0; i < uint8Array.length; i++) {
        uint8Array[i] = byteArray[i];
      }

      return uint8Array;
    },
    handleClickedRequirement(item) {
      localStorage.setItem('inScopeRequirement', JSON.stringify(item));
      this.$router.push({ name: 'TreeView' });
    },
    handleEditRequirement(item) {
      localStorage.setItem('inScopeRequirement', JSON.stringify(item));
      this.$router.push({ name: 'Claims' });
    },
    handleManualClick(createCaeItem) {
      localStorage.setItem('inScopeRequirement', JSON.stringify(createCaeItem));
      this.$router.push({ name: 'Claims' });
    },
    statusChipColor(status) {
      switch (status) {
        case 'Draft':
          return 'orange';
        case 'Pending':
          return 'primary';
        case 'Completed':
          return 'blue';
        default:
          return 'primary';
      }
    },
    navigateToChat(item) {
      localStorage.setItem('chatData', JSON.stringify({ requirement: item, soc: this.socData }));
      this.$router.push({
        name: 'SacComposerChat',
        query: { requirementId: item.id },
      });
    },

    customFilter(value, search, item) {
      if (!search) return true;

      search = search.toString().toLowerCase();

      if (this.searchField === 'all') {
        // Search in all fields (default behavior)
        return Object.keys(item).some(key => {
          // Skip action column and non-string values
          if (key === 'actions' || key === 'raw' || item[key] === null || item[key] === undefined) {
            return false;
          }

          try {
            const itemValue = item[key].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        });
      } else {
        // Search only in the selected field
        if (item[this.searchField] !== null && item[this.searchField] !== undefined) {
          try {
            const itemValue = item[this.searchField].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        }
        return false;
      }
    },

    applySearch() {
      // This method can be used for any additional search logic if needed
      // For now it's a placeholder as the filtering happens automatically
    },
    openReviewModal(item) {
      this.selectedRequirement = item;
      this.reviewModalOpen = true;
    },
  },
};
</script>

<style scoped>
/* ── Content Wrapper ── */
.rl-content-wrapper {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}
/* ── Back Button ── */
.rl-back-btn {
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em;
  padding: 8px 20px !important;
  text-transform: none !important;
  font-size: 0.95rem !important;
  border: 2px solid currentColor !important;
  transition: all 0.25s ease !important;
}
.rl-back-btn:hover {
  background: #e8eaf6 !important;
  transform: translateX(-3px);
}
/* ── Search Bar ── */
.rl-search-bar-container {
  border-radius: 12px !important;
  overflow: hidden;
  margin: 16px 0;
  border: none !important;
}
.rl-search-bar-header {
  background: linear-gradient(135deg, #37474f 0%, #455a64 50%, #546e7a 100%);
  padding: 10px 20px;
  display: flex;
  align-items: center;
}
.rl-search-bar-body {
  padding: 16px 20px 8px;
}

/* ── Table Card ── */
.rl-table-card {
  border-radius: 12px !important;
  overflow: hidden;
  border: none !important;
}
.rl-table-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%);
  padding: 12px 20px;
  display: flex;
  align-items: center;
}

/* ── Dialog Card ── */
.rl-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

/* ── Review Modal Header ── */
.rl-review-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
  width: 100%;
  margin: 0;
  padding: 16px 24px !important;
}

/* ── Info Cards (Review Modal) ── */
.info-card {
  border: 1px solid #e8eaf6 !important;
  border-radius: 12px !important;
  transition: all 0.25s ease;
  background: #f8f9ff;
}
.info-card:hover {
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(26, 35, 126, 0.08) !important;
  transform: translateY(-2px);
}

/* ── Search Field ── */
.search-field {
  min-width: 150px;
}

/* ── Utility ── */
.gap-1 {
  gap: 4px;
}

/* ── AI Sparkle Icon ── */
.ai-sparkle-icon {
  color: #7c3aed !important;
  transition: transform 0.25s ease, color 0.25s ease;
}
.ai-sparkle-icon:hover {
  color: #a855f7 !important;
  transform: scale(1.2);
}

/* ── CAE Dialog ── */
.cae-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
  background: #fafbfd !important;
}
.cae-dialog-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.cae-header-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.cae-req-banner {
  background: #eef2ff;
  border-bottom: 1px solid #dde3f7;
  padding: 12px 20px;
}
.cae-req-divider {
  color: #aab4d4;
  font-weight: 300;
}
.cae-req-title {
  color: #455a64;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 340px;
}
.cae-method-card {
  border-radius: 12px !important;
  border: 2px solid #e8eaf6 !important;
  cursor: pointer;
  transition: all 0.25s ease;
  height: 100%;
}
.cae-method-card:hover {
  border-color: #3949ab !important;
  box-shadow: 0 6px 20px rgba(57, 73, 171, 0.12) !important;
  transform: translateY(-2px);
}
.cae-method-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}
.cae-dialog-footer {
  border-top: 1px solid #e8eaf6;
  padding: 10px 20px;
  display: flex;
  justify-content: flex-end;
  background: #f5f7ff;
}
</style>
